import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { disableHiddenStandardControls, isStandardFieldHidden } from "../../standard-field-visibility.helper";
import { FormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { ToastService } from "@core/components/toasts/toasts.service";
import { pagesActions } from "app/page-access";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ApprovalScopeService } from "services/corporate/approval-scope.service";
import { EmployeeService } from "services/corporate/employee.service";
import { AuthService } from "services/auth.service";
import { DomainService } from "services/domain.service";
import { ApprovalScopeActionType } from "services/enums/approvalScopeActionType.enum";
import { PromotionRequestType } from "services/enums/PromotionRequestType.enum";
import { UnhealthinessType } from "services/enums/UnhealthinessType.enum";
import { WorkforceplanMovementType } from "services/enums/WorkforceplanMovementType.enum";
import { JobTitleGroupService } from "services/hiring/jobTitleGroup.service";
import { JobTitleService } from "services/hiring/jobTitle.service";
import { JobTitleSeniorizationService } from "services/hiring/jobTitleSeniorization.service";
import { MeritPromotionSettingsModel } from "services/models/corporate/approval-scope/merit-promotion-settings.model";
import { JobTitleGroupModel } from "services/models/hiring/jobTitleGroup.model";
import { JobTitleModel } from "services/models/hiring/jobTitle.model";
import { JobTitleSeniorizationModel } from "services/models/hiring/jobTitleSeniorization.model";
import { MovementTypeModel } from "services/models/movementType.model";
import { OptionModel } from "services/models/model";
import { Filter } from "services/models/odata/filter";
import { QueryParams } from "services/models/odata/queryParams";
import { WorkforcePlanTeamEmployeeModel } from "services/models/workforceplan";
import { WorkforceplanMovementTypeService } from "services/workforceplan/workforceplan-movement-type.service";
import { guidEmpty } from "utils/helps";
import {
  DrawerDataInput,
  FormRequestPromotionPayload,
  FormRequestPromotionValues,
  ISolicitationFormComponent,
  MovementSalaryRange,
} from "../../form-types";
import { normalizeSalaryValue } from "@core/utils/currency-value.helper";
import {
  buildErpApprovalFieldVisibilityParams,
  canShowExecutionDate as resolveCanShowExecutionDate,
  canShowTradeUnionField as resolveCanShowTradeUnionField,
} from "../../erp-approval-field-visibility.helper";

@Component({
  selector: "app-promotion-form",
  templateUrl: "./promotion-form.component.html",
  styleUrls: ["./promotion-form.component.scss"],
})
export class PromotionFormComponent implements OnChanges, OnDestroy, ISolicitationFormComponent {
  @Input() data: DrawerDataInput | null = null;
  @Input() source: "action-required" | null = null;
  @Input() hiddenStandardFields: string[] = [];
  @Output() employeeLoadingChange = new EventEmitter<boolean>();

  public form: UntypedFormGroup;
  public promotionTypeOptions = PromotionRequestType.buildListEnum();
  public currentJobTitleOptions: OptionModel[] = [];
  public jobTitleGroupOptions: OptionModel[] = [];
  public jobTitleOptions: OptionModel[] = [];
  public movementTypeOptions: OptionModel[] = [];
  public isLoadingJobTitleGroups = false;
  public isLoadingJobTitles = false;
  public canManageEmploymentContractInformation = false;
  public unhealthinessOptions = UnhealthinessType.buildListEnum();
  public meritSettingsLoadError = false;

  private readonly destroy$ = new Subject<void>();
  private allJobTitleGroups: JobTitleGroupModel[] = [];
  private availableGroups: JobTitleGroupModel[] = [];
  private availableJobTitles: JobTitleModel[] = [];
  private seniorizations: JobTitleSeniorizationModel[] = [];
  private movementTypes: MovementTypeModel[] = [];
  private meritSettings = MeritPromotionSettingsModel.create({});
  private isInitializing = false;
  private isMeritSettingsLoading = false;
  private hasLoadedMeritSettings = false;
  private hasLoadedJobTitleGroups = false;
  private hasLoadedSeniorizations = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly approvalScopeService: ApprovalScopeService,
    private readonly employeeService: EmployeeService,
    private readonly jobTitleGroupService: JobTitleGroupService,
    private readonly jobTitleService: JobTitleService,
    private readonly jobTitleSeniorizationService: JobTitleSeniorizationService,
    private readonly movementTypeService: WorkforceplanMovementTypeService,
    private readonly authService: AuthService,
    private readonly toastService: ToastService
  ) {
    this.form = this.formBuilder.group({
      note: [null, Validators.required],
      movementType: [WorkforceplanMovementType.PROMOTION.id],
      employeeId: [null, Validators.required],
      workScaleGroup: [null, Validators.required],
      workforcePlanId: [null, Validators.required],
      effectiveDate: [null],
      promotionType: [null, Validators.required],
      changeTypeCode: [],
      newSalary: [],
      currentJobTitleId: [null],
      currentJobTitleName: [null],
      jobTitleGroupId: [null, Validators.required],
      jobTitleGroupName: [null, Validators.required],
      jobTitleId: [null],
      jobTitleName: [null],
      executionDate: [null],
      salaryRange: [null],
      hasDangerousness: [false],
      unhealthinessType: [null],
    });

    this.canManageEmploymentContractInformation = this.authService.hasActionAllowed(
      pagesActions.workforcePlan.canManageEmploymentContractInformation,
      "/workforceplan",
      true
    );

    this.form.get("promotionType")?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.data || this.isInitializing) {
        return;
      }

      this.onPromotionTypeChange();
    });
  }

  public isHidden(fieldKey: string): boolean {
    return isStandardFieldHidden(this.hiddenStandardFields, fieldKey);
  }

  private applyHiddenStandardFields(): void {
    disableHiddenStandardControls(this.form, this.hiddenStandardFields);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const dataChanged = !!changes["data"]?.currentValue;
    if (dataChanged && this.data?.type === "REQUEST_PROMOTION") {
      this.initializeForm();
    }

    if (changes["hiddenStandardFields"] && this.form && !dataChanged) {
      this.applyHiddenStandardFields();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getForm(): UntypedFormGroup {
    return this.form;
  }

  public getPayload(): FormRequestPromotionPayload {
    const value = this.form.getRawValue() as FormRequestPromotionValues;
    return {
      note: value.note,
      employeeId: value.employeeId,
      movementType: value.movementType,
      workScaleGroup: value.workScaleGroup,
      workforcePlanId: value.workforcePlanId,
      effectiveDate: value.effectiveDate,
      payload: JSON.stringify({
        promotionType: this.normalizePromotionType(value.promotionType),
        changeTypeCode: value.changeTypeCode,
        newSalary: normalizeSalaryValue(value.newSalary),
        currentJobTitleId: value.currentJobTitleId,
        currentJobTitleName: value.currentJobTitleName,
        jobTitleGroupId: value.jobTitleGroupId,
        jobTitleGroupName: value.jobTitleGroupName,
        jobTitleId: value.jobTitleId,
        jobTitleName: value.jobTitleName,
        executionDate: value.executionDate,
        salaryRange: value.salaryRange ?? null,
        ...(this.isHidden("hasDangerousness") ? {} : { hasDangerousness: value.hasDangerousness }),
        ...(this.isHidden("unhealthinessType") ? {} : { unhealthinessType: value.unhealthinessType }),
      }),
    };
  }

  public getUpdatePayload(): string {
    const value = this.form.getRawValue() as FormRequestPromotionValues;
    return JSON.stringify({
      changeTypeCode: value.changeTypeCode,
      newSalary: normalizeSalaryValue(value.newSalary),
      executionDate: value.executionDate,
      salaryRange: value.salaryRange ?? null,
      ...(this.isHidden("hasDangerousness") ? {} : { hasDangerousness: value.hasDangerousness }),
      ...(this.isHidden("unhealthinessType") ? {} : { unhealthinessType: value.unhealthinessType }),
    });
  }

  /** Solicitação criada antes da faixa existir não fica bloqueada na homologação. */
  public get isSalaryRangeRequired(): boolean {
    return !this.isDetailsMode || !!this.data?.solicitationDetails?.payloadData?.salaryRange;
  }

  /** Com faixa salarial, o salário é o valor da faixa e o campo fica oculto. */
  public get hasSalaryRange(): boolean {
    return !!this.form.get("salaryRange")?.value;
  }

  public onSalaryRangeSelected(range: MovementSalaryRange | null): void {
    if (range && this.canEditFieldsToApprovePromotion) {
      this.form.get("newSalary")?.setValue(range.amount);
    }
  }

  public onPromotionTypeChange(): void {
    const employee = this.data?.employee;
    const defaultJobTitleGroupId = employee?.jobTitleGroupId ?? null;
    const defaultJobTitleGroupName = employee?.jobTitleGroupName ?? null;

    this.form.patchValue(
      {
        changeTypeCode: this.resolveChangeTypeCode(this.form.get("promotionType")?.value),
        jobTitleGroupId: defaultJobTitleGroupId,
        jobTitleGroupName: defaultJobTitleGroupName,
      },
      { emitEvent: false }
    );
    this.seedJobTitleGroupOptionFromEmployee();
    this.syncJobTitleGroupLockState();
    this.applyDestinationJobTitleState();
  }

  public onJobTitleChange(selected: OptionModel | string | null): void {
    const option = this.resolveSelectedOption(this.jobTitleOptions, selected);

    if (!option?.value) {
      this.form.patchValue({
        jobTitleId: null,
        jobTitleName: null,
      });
      return;
    }

    const selectedJobTitle = this.availableJobTitles.find(jobTitle => jobTitle.id === option.value);
    this.form.patchValue({
      jobTitleId: option.value,
      jobTitleName: selectedJobTitle?.title || this.extractOptionName(option.label),
    });
  }

  private applyDestinationJobTitleState(): void {
    const jobTitleControl = this.form.get("jobTitleId");
    const jobTitleNameControl = this.form.get("jobTitleName");
    const currentJobTitleId = this.form.get("currentJobTitleId")?.value as string | null;
    const currentJobTitleName = this.form.get("currentJobTitleName")?.value as string | null;
    const jobTitleGroupId = this.form.get("jobTitleGroupId")?.value as string | null;

    if (this.isMeritPromotionSelected) {
      if (!this.isDetailsMode) {
        this.jobTitleOptions = [];
        this.availableJobTitles = [];
        this.form.patchValue(
          {
            jobTitleId: currentJobTitleId,
            jobTitleName: currentJobTitleName,
          },
          { emitEvent: false }
        );
      }

      jobTitleControl?.clearValidators();
      jobTitleControl?.updateValueAndValidity({ emitEvent: false });
      jobTitleNameControl?.clearValidators();
      jobTitleNameControl?.updateValueAndValidity({ emitEvent: false });
      this.applyHiddenStandardFields();
      return;
    }

    if (this.isSeniorityPromotionSelected) {
      if (!this.isDetailsMode) {
        this.form.patchValue(
          {
            jobTitleId: null,
            jobTitleName: null,
          },
          { emitEvent: false }
        );
        jobTitleControl?.setValidators([Validators.required]);
        jobTitleNameControl?.setValidators([Validators.required]);

        if (jobTitleGroupId) {
          this.loadJobTitles(jobTitleGroupId);
        }
      }

      jobTitleControl?.updateValueAndValidity({ emitEvent: false });
      jobTitleNameControl?.updateValueAndValidity({ emitEvent: false });
      this.applyHiddenStandardFields();
      return;
    }

    if (!this.isDetailsMode) {
      this.jobTitleOptions = [];
      this.availableJobTitles = [];
      this.form.patchValue(
        {
          jobTitleId: null,
          jobTitleName: null,
        },
        { emitEvent: false }
      );
    }

    jobTitleControl?.clearValidators();
    jobTitleControl?.updateValueAndValidity({ emitEvent: false });
    jobTitleNameControl?.clearValidators();
    jobTitleNameControl?.updateValueAndValidity({ emitEvent: false });
    this.applyHiddenStandardFields();
  }

  private initializeForm(): void {
    this.isInitializing = true;
    this.hasLoadedMeritSettings = false;
    this.isMeritSettingsLoading = false;
    this.hasLoadedJobTitleGroups = false;
    this.hasLoadedSeniorizations = false;
    this.allJobTitleGroups = [];
    this.seniorizations = [];
    this.resetApprovalFieldValidators();
    this.ensureEmployeeLoaded();
    this.loadMovementTypes();

    if (this.isDetailsMode) {
      this.buildDetailsForm();
    } else {
      this.buildAddForm();
      this.loadSeniorizations();
      this.loadJobTitleGroups();
      this.loadMeritSettings();
    }

    if (this.canEditFieldsToApprovePromotion) {
      const newSalaryCtrl = this.form.get("newSalary");
      const executionDateCtrl = this.form.get("executionDate");

      newSalaryCtrl?.enable();
      newSalaryCtrl?.setValidators([Validators.required]);
      executionDateCtrl?.enable();
      executionDateCtrl?.setValidators([Validators.required]);
      this.form.get("salaryRange")?.enable();

      const savedSalaryRange = this.data?.solicitationDetails?.payloadData?.salaryRange;
      if (savedSalaryRange && newSalaryCtrl?.value == null) {
        newSalaryCtrl?.setValue(savedSalaryRange.amount);
      }

      this.form.updateValueAndValidity();
    }

    if (this.canEditDangerousness) {
      this.form.get("hasDangerousness")?.enable();
    }

    if (this.canEditUnhealthiness) {
      const unhealthinessTypeCtrl = this.form.get("unhealthinessType");
      unhealthinessTypeCtrl?.enable();
      unhealthinessTypeCtrl?.setValidators([Validators.required]);
      unhealthinessTypeCtrl?.updateValueAndValidity({ emitEvent: false });
    }

    this.applyHiddenStandardFields();
    this.isInitializing = false;
  }

  private buildAddForm(): void {
    const employee = this.data?.employee;

    this.form.patchValue(
      {
        note: null,
        movementType: WorkforceplanMovementType.PROMOTION.id,
        employeeId: employee?.id ?? null,
        workScaleGroup: this.data?.workScaleGroup ?? null,
        workforcePlanId: this.data?.workforcePlanId ?? null,
        promotionType: null,
        changeTypeCode: null,
        newSalary: null,
        currentJobTitleId: employee?.jobTitleId ?? null,
        currentJobTitleName: employee?.jobTitleName ?? null,
        jobTitleGroupId: employee?.jobTitleGroupId ?? null,
        jobTitleGroupName: employee?.jobTitleGroupName ?? null,
        jobTitleId: null,
        jobTitleName: null,
        executionDate: null,
        salaryRange: null,
        hasDangerousness: false,
        unhealthinessType: null,
      },
      { emitEvent: false }
    );

    this.syncCurrentJobTitleOptions();
    this.seedJobTitleGroupOptionFromEmployee();
    this.syncJobTitleGroupLockState();
  }

  private seedJobTitleGroupOptionFromEmployee(): void {
    const employee = this.data?.employee;
    if (!employee?.jobTitleGroupId) {
      return;
    }

    const label = employee.jobTitleGroupName || employee.jobTitleGroupId;

    if (!this.jobTitleGroupOptions.some(option => option.value === employee.jobTitleGroupId)) {
      this.jobTitleGroupOptions = [{ label, value: employee.jobTitleGroupId }];
    }
  }

  private buildDetailsForm(): void {
    const solicitation = this.data?.solicitation;
    const payloadData = this.data?.solicitationDetails?.payloadData;

    this.form.patchValue(
      {
        note: this.data?.solicitationDetails?.note ?? null,
        movementType: solicitation?.movementTypeData?.id ?? WorkforceplanMovementType.PROMOTION.id,
        employeeId: solicitation?.employeeId ?? this.data?.employee?.id ?? null,
        workScaleGroup: solicitation?.workScaleGroup ?? this.data?.workScaleGroup ?? null,
        workforcePlanId: this.data?.workforcePlanId ?? null,
        effectiveDate: solicitation?.effectiveDate?.substring(0, 10) ?? null,
        promotionType: this.normalizePromotionType(payloadData?.promotionType),
        changeTypeCode:
          payloadData?.changeTypeCode ??
          this.resolveChangeTypeCode(this.normalizePromotionType(payloadData?.promotionType)),
        newSalary: normalizeSalaryValue(payloadData?.newSalary),
        currentJobTitleId: payloadData?.currentJobTitleId ?? null,
        currentJobTitleName: payloadData?.currentJobTitleName ?? null,
        jobTitleGroupId: payloadData?.jobTitleGroupId ?? null,
        jobTitleGroupName: payloadData?.jobTitleGroupName ?? null,
        jobTitleId: payloadData?.jobTitleId ?? null,
        jobTitleName: payloadData?.jobTitleName ?? null,
        executionDate: payloadData?.executionDate ? String(payloadData.executionDate).substring(0, 10) : null,
        salaryRange: payloadData?.salaryRange ?? null,
        hasDangerousness: payloadData?.hasDangerousness ?? false,
        unhealthinessType: UnhealthinessType.resolveValue(payloadData?.unhealthinessType),
      },
      { emitEvent: false }
    );

    this.syncCurrentJobTitleOptions();
    this.patchOptionsFromSolicitationDetails();
    this.applyDestinationJobTitleState();

    if (!this.data?.solicitationDetails?.permission?.canUpdate) {
      this.form.disable();
      return;
    }

    this.disableDetailFieldsByDefault();
  }

  private loadMeritSettings(): void {
    if (!this.data?.companyId || this.isDetailsMode || this.isMeritSettingsLoading || this.hasLoadedMeritSettings) {
      return;
    }

    this.isMeritSettingsLoading = true;

    this.approvalScopeService
      .getMeritPromotionSettings(this.data.companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: settings => {
          this.isMeritSettingsLoading = false;
          this.hasLoadedMeritSettings = true;
          this.meritSettingsLoadError = false;
          this.meritSettings = settings;
          this.applyJobTitleGroupsFilter();
        },
        error: () => {
          this.isMeritSettingsLoading = false;
          this.hasLoadedMeritSettings = true;
          this.meritSettingsLoadError = true;
          this.toastService.showError(
            "Não foi possível carregar as regras de mérito. A seleção por mérito foi bloqueada para evitar grupos indevidos.",
            "top-center"
          );
          this.applyJobTitleGroupsFilter();
        },
      });
  }

  private loadMovementTypes(): void {
    this.movementTypeService
      .listMovementTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.movementTypes = response?.data || [];
          this.movementTypeOptions = this.movementTypes.map(item => ({
            label: `${item.foreignId} - ${item.name}`,
            value: item.foreignId,
          }));
          this.form.patchValue({
            changeTypeCode:
              this.form.get("changeTypeCode")?.value ??
              this.resolveChangeTypeCode(this.form.get("promotionType")?.value),
          });
        },
        error: () => {
          this.movementTypes = [];
          this.movementTypeOptions = [];
        },
      });
  }

  private loadJobTitleGroups(onComplete?: () => void): void {
    if (!this.data?.companyId || this.isDetailsMode) {
      return;
    }

    if (this.hasLoadedJobTitleGroups) {
      this.applyJobTitleGroupsFilter();
      onComplete?.();
      return;
    }

    this.isLoadingJobTitleGroups = true;
    this.jobTitleGroupService
      .getJobTitleGroupsByCompany(this.data.companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: groups => {
          this.allJobTitleGroups = groups || [];
          this.hasLoadedJobTitleGroups = true;
          this.applyJobTitleGroupsFilter();
          this.isLoadingJobTitleGroups = false;
          onComplete?.();
        },
        error: () => {
          this.toastService.showError("Não foi possível carregar os grupos de cargo.", "top-center");
          this.isLoadingJobTitleGroups = false;
        },
      });
  }

  private applyJobTitleGroupsFilter(): void {
    if (!this.hasLoadedJobTitleGroups) {
      return;
    }

    const employee = this.data?.employee;

    if (employee?.jobTitleGroupId) {
      const employeeGroup =
        this.allJobTitleGroups.find(group => group.id === employee.jobTitleGroupId) ?? null;

      this.availableGroups = employeeGroup ? [employeeGroup] : [];
      this.jobTitleGroupOptions = [
        {
          label: employeeGroup
            ? `${employeeGroup.code} - ${employeeGroup.name}`
            : employee.jobTitleGroupName || employee.jobTitleGroupId,
          value: employee.jobTitleGroupId,
        },
      ];

      this.form.patchValue(
        {
          jobTitleGroupId: employee.jobTitleGroupId,
          jobTitleGroupName: employee.jobTitleGroupName ?? employeeGroup?.name ?? null,
        },
        { emitEvent: false }
      );
    } else {
      this.availableGroups = this.filterGroupsByPromotionType(this.allJobTitleGroups);
      this.jobTitleGroupOptions = this.availableGroups.map(group => ({
        label: `${group.code} - ${group.name}`,
        value: group.id,
      }));
      this.syncSelectedJobTitleGroupOption();
    }

    this.syncJobTitleGroupLockState();

    const jobTitleGroupId = this.form.get("jobTitleGroupId")?.value;
    if (jobTitleGroupId && !this.isDetailsMode && this.isSeniorityPromotionSelected) {
      this.loadJobTitles(jobTitleGroupId);
    }
  }

  private loadSeniorizations(): void {
    if (this.isDetailsMode || this.hasLoadedSeniorizations) {
      return;
    }

    this.jobTitleSeniorizationService
      .list(
        {
          select: ["id", "name", "level"],
          orderBy: "level",
          orderByOrder: "asc",
          filter: Filter.equal("enable", true),
        } as QueryParams,
        true
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.seniorizations = response?.data || [];
          this.hasLoadedSeniorizations = true;

          const jobTitleGroupId = this.form.get("jobTitleGroupId")?.value;
          if (jobTitleGroupId && this.isSeniorityPromotionSelected) {
            this.loadJobTitles(jobTitleGroupId);
          }
        },
        error: () => {
          this.seniorizations = [];
          this.hasLoadedSeniorizations = true;
          this.toastService.showError(
            "Não foi possível carregar os níveis de senioridade.",
            "top-center"
          );

          const jobTitleGroupId = this.form.get("jobTitleGroupId")?.value;
          if (jobTitleGroupId && this.isSeniorityPromotionSelected) {
            this.loadJobTitles(jobTitleGroupId);
          }
        },
      });
  }

  private loadJobTitles(jobTitleGroupId: string): void {
    if (!this.data?.companyId || !jobTitleGroupId || this.isDetailsMode) {
      return;
    }

    if (this.isSeniorityPromotionSelected && !this.hasLoadedSeniorizations) {
      this.isLoadingJobTitles = true;
      return;
    }

    this.isLoadingJobTitles = true;
    this.jobTitleService
      .getJobTitlesByTitleAndGroup(this.data.companyId, jobTitleGroupId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: jobTitles => {
          const enabledJobTitles = jobTitles || [];
          const currentJobTitleId = this.form.get("currentJobTitleId")?.value as string | null;

          this.availableJobTitles = this.isSeniorityPromotionSelected
            ? this.filterJobTitlesByHigherSeniority(enabledJobTitles, currentJobTitleId)
            : enabledJobTitles;

          this.jobTitleOptions = this.availableJobTitles.map(jobTitle => ({
            label: `${jobTitle.code} - ${jobTitle.title}`,
            value: jobTitle.id,
          }));

          if (this.form.get("jobTitleId")?.value) {
            this.ensureSelectedJobTitleOption();
          }

          this.isLoadingJobTitles = false;
          this.notifyIfNoHigherSeniorityJobTitles();
        },
        error: () => {
          this.toastService.showError("Não foi possível carregar as funções de destino.", "top-center");
          this.isLoadingJobTitles = false;
        },
      });
  }

  private notifyIfNoHigherSeniorityJobTitles(): void {
    if (
      this.isDetailsMode ||
      !this.isSeniorityPromotionSelected ||
      !this.hasLoadedSeniorizations ||
      !this.form.get("jobTitleGroupId")?.value ||
      this.shouldShowNoSeniorityGroupAlert ||
      this.jobTitleOptions.length
    ) {
      return;
    }

    this.toastService.showWarning(
      "Não há funções com senioridade superior disponíveis neste grupo de cargo.",
      "top-right"
    );
  }

  private filterJobTitlesByHigherSeniority(
    jobTitles: JobTitleModel[],
    currentJobTitleId: string | null
  ): JobTitleModel[] {
    const currentLevel = this.resolveJobTitleSeniorityLevel(jobTitles, currentJobTitleId);
    if (currentLevel == null) {
      return [];
    }

    const candidates = jobTitles.filter(jobTitle => {
      if (!jobTitle.jobTitleSeniorizationId) {
        return false;
      }

      if (currentJobTitleId && jobTitle.id === currentJobTitleId) {
        return false;
      }

      return this.getSeniorizationLevel(jobTitle.jobTitleSeniorizationId) != null;
    });

    const higherLevel = candidates.filter(jobTitle => {
      const level = this.getSeniorizationLevel(jobTitle.jobTitleSeniorizationId!);
      return level != null && level > currentLevel;
    });

    if (higherLevel.length > 0) {
      return higherLevel;
    }

    // No nível máximo: permite outras funções do mesmo nível (ex.: Sr → outros Sr).
    return candidates.filter(jobTitle => {
      const level = this.getSeniorizationLevel(jobTitle.jobTitleSeniorizationId!);
      return level != null && level === currentLevel;
    });
  }

  private resolveJobTitleSeniorityLevel(
    jobTitles: JobTitleModel[],
    jobTitleId: string | null
  ): number | null {
    if (!jobTitleId) {
      return null;
    }

    const currentJobTitle = jobTitles.find(jobTitle => jobTitle.id === jobTitleId);
    if (!currentJobTitle?.jobTitleSeniorizationId) {
      return null;
    }

    return this.getSeniorizationLevel(currentJobTitle.jobTitleSeniorizationId);
  }

  private getSeniorizationLevel(seniorizationId: string): number | null {
    const seniorization = this.seniorizations.find(item => item.id === seniorizationId);
    return seniorization?.level ?? null;
  }

  private syncJobTitleGroupLockState(): void {
    const control = this.form.get("jobTitleGroupId");
    if (!control || this.isDetailsMode) {
      return;
    }

    control.disable({ emitEvent: false });
  }

  private get isSeniorityPromotionSelected(): boolean {
    return (
      this.normalizePromotionType(this.form.get("promotionType")?.value) ===
      PromotionRequestType.SENIORITY.value
    );
  }

  private get isMeritPromotionSelected(): boolean {
    return (
      this.normalizePromotionType(this.form.get("promotionType")?.value) ===
      PromotionRequestType.MERIT.value
    );
  }

  public get shouldShowDestinationJobTitle(): boolean {
    return this.isSeniorityPromotionSelected;
  }

  private filterGroupsByPromotionType(groups: JobTitleGroupModel[]): JobTitleGroupModel[] {
    const selectedPromotionType = this.normalizePromotionType(this.form.get("promotionType")?.value);

    if (!selectedPromotionType) {
      return groups;
    }

    if (selectedPromotionType === PromotionRequestType.SENIORITY.value) {
      return groups.filter(group => group.hasSeniority);
    }

    if (this.meritSettingsLoadError) {
      return [];
    }

    if (!this.hasLoadedMeritSettings) {
      return groups;
    }

    const excludedIds = this.meritSettings?.excludedJobTitleGroupIds || [];
    return groups.filter(group => !excludedIds.includes(group.id));
  }

  private resolveChangeTypeCode(promotionType: string | null | undefined): string | null {
    const normalized = this.normalizePromotionType(promotionType);
    if (!normalized || !this.movementTypes.length) {
      return null;
    }

    const normalizedPromotionType = this.normalizeText(normalized);
    const isMerit = normalizedPromotionType === this.normalizeText(PromotionRequestType.MERIT.value);
    const isSeniority = normalizedPromotionType === this.normalizeText(PromotionRequestType.SENIORITY.value);

    const movementType = this.movementTypes.find(item => {
      const normalizedName = this.normalizeText(item.name);

      if (isMerit) {
        return normalizedName.includes("MERIT");
      }

      if (isSeniority) {
        return normalizedName.includes("SENIOR");
      }

      return false;
    });

    return movementType?.foreignId || null;
  }

  private patchOptionsFromSolicitationDetails(): void {
    const payloadData = this.data?.solicitationDetails?.payloadData;
    if (!payloadData) {
      return;
    }

    if (payloadData.jobTitleGroupId && payloadData.jobTitleGroupName) {
      this.jobTitleGroupOptions = [
        { label: payloadData.jobTitleGroupName, value: payloadData.jobTitleGroupId },
      ];
    }

    if (payloadData.jobTitleId && payloadData.jobTitleName) {
      this.jobTitleOptions = [{ label: payloadData.jobTitleName, value: payloadData.jobTitleId }];
    }
  }

  private syncSelectedJobTitleGroupOption(): void {
    const jobTitleGroupId = this.form.get("jobTitleGroupId")?.value;
    const jobTitleGroupName = this.form.get("jobTitleGroupName")?.value;
    if (!jobTitleGroupId || !jobTitleGroupName) {
      return;
    }

    if (this.isDetailsMode) {
      if (!this.jobTitleGroupOptions.some(option => option.value === jobTitleGroupId)) {
        this.jobTitleGroupOptions = [
          ...this.jobTitleGroupOptions,
          { label: jobTitleGroupName, value: jobTitleGroupId },
        ];
      }
      return;
    }

    const isAllowed = this.availableGroups.some(group => group.id === jobTitleGroupId);
    if (!isAllowed) {
      this.form.patchValue({
        jobTitleGroupId: null,
        jobTitleGroupName: null,
        jobTitleId: null,
        jobTitleName: null,
      });
      this.jobTitleOptions = [];
      return;
    }

    if (!this.jobTitleGroupOptions.some(option => option.value === jobTitleGroupId)) {
      this.jobTitleGroupOptions = [
        ...this.jobTitleGroupOptions,
        { label: jobTitleGroupName, value: jobTitleGroupId },
      ];
    }
  }

  private ensureSelectedJobTitleOption(): void {
    const jobTitleId = this.form.get("jobTitleId")?.value;
    const jobTitleName = this.form.get("jobTitleName")?.value;
    if (!jobTitleId || !jobTitleName) {
      return;
    }

    if (!this.jobTitleOptions.some(option => option.value === jobTitleId)) {
      this.jobTitleOptions = [
        ...this.jobTitleOptions,
        { label: jobTitleName, value: jobTitleId },
      ];
    }
  }

  private ensureEmployeeLoaded(): void {
    if (!this._isEmployeePartial()) {
      return;
    }

    this.employeeLoadingChange.emit(true);
    this.employeeService
      .get(this.data!.employee.id, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.data!.employee = {
            ...this.data!.employee,
            companyId: response.data.companyId,
            centerCostId: response.data.costCenterId,
            costCenterName: response.data.costCenterName,
            jobTitleId: response.data.jobTitleId,
            jobTitleName: response.data.jobTitleName,
            jobTitleGroupId: this.data!.employee.jobTitleGroupId,
            jobTitleGroupName: this.data!.employee.jobTitleGroupName,
            registration: response.data.registration,
          } as WorkforcePlanTeamEmployeeModel;
          if (!this.data?.companyId && !this.isDetailsMode) {
            this.data!.companyId = response.data.companyId;
            this.loadSeniorizations();
            this.loadJobTitleGroups();
            this.loadMeritSettings();
          }

          if (this.isDetailsMode) {
            this.employeeLoadingChange.emit(false);
            return;
          }

          this.form.patchValue({
            employeeId: response.data.id,
            currentJobTitleId: response.data.jobTitleId,
            currentJobTitleName: response.data.jobTitleName,
          });
          this.syncCurrentJobTitleOptions();
          if (this.isMeritPromotionSelected) {
            this.applyDestinationJobTitleState();
          }
          this.employeeLoadingChange.emit(false);
        },
        error: () => {
          this.toastService.showError("Não foi possível carregar os dados do colaborador.", "top-center");
          this.employeeLoadingChange.emit(false);
        },
      });
  }

  private _isEmployeePartial(): boolean {
    const employee = this.data?.employee;
    if (!employee?.id || employee.id === guidEmpty) {
      return false;
    }

    return !employee.jobTitleId || !employee.jobTitleName;
  }

  private extractOptionName(label: string): string {
    return (label || "").split(" - ").slice(1).join(" - ") || label;
  }

  private resolveSelectedOption(options: OptionModel[], selected: OptionModel | string | null): OptionModel | null {
    if (!selected) {
      return null;
    }

    if (typeof selected === "object" && "value" in selected) {
      return selected;
    }

    return options.find(option => option.value === selected) ?? null;
  }

  private normalizeText(value: string): string {
    return (value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toUpperCase();
  }

  private normalizePromotionType(value: string | null | undefined): string | null {
    return PromotionRequestType.getByAny(value)?.value ?? null;
  }

  private resetApprovalFieldValidators(): void {
    [
      "newSalary",
      "changeTypeCode",
      "executionDate",
      "unhealthinessType",
    ].forEach(controlName => {
      const control = this.form.get(controlName);
      control?.clearValidators();
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private disableDetailFieldsByDefault(): void {
    [
      "note",
      "promotionType",
      "jobTitleGroupId",
      "jobTitleId",
      "changeTypeCode",
      "newSalary",
      "executionDate",
      "salaryRange",
      "hasDangerousness",
      "unhealthinessType",
    ].forEach(controlName => {
      this.form.get(controlName)?.disable({ emitEvent: false });
    });
  }

  public getSelectedOptionText(options: OptionModel[] | undefined, value: unknown): string | null {
    if (!options?.length || value == null) {
      return null;
    }

    const option = options.find(item => item.value === value || item.label === value);
    return option?.label ?? null;
  }

  private syncCurrentJobTitleOptions(): void {
    const currentJobTitleId = this.form.get("currentJobTitleId")?.value;
    const currentJobTitleName = this.form.get("currentJobTitleName")?.value;

    if (!currentJobTitleId || !currentJobTitleName) {
      this.currentJobTitleOptions = [];
      return;
    }

    this.currentJobTitleOptions = [
      {
        label: currentJobTitleName,
        value: currentJobTitleId,
      },
    ];
  }

  public get isAddMode(): boolean {
    return this.data?.mode === "add";
  }

  public get isDetailsMode(): boolean {
    return this.data?.mode === "details";
  }

  public get shouldShowEmployeeInfo(): boolean {
    return this.isAddMode && !!this.data?.employee?.name;
  }

  public get isScenarioSelectionReadonly(): boolean {
    return this.isDetailsMode;
  }

  public get showJobTitleGroupsLoading(): boolean {
    return !this.isDetailsMode && this.isLoadingJobTitleGroups;
  }

  public get showJobTitlesLoading(): boolean {
    return !this.isDetailsMode && this.isLoadingJobTitles;
  }

  public get canShowFieldsToChangeJobTitle(): boolean {
    if (this.canEditFieldsToApprovePromotion) {
      return true;
    }

    const payload = this.data?.solicitationDetails?.payloadData;
    return (
      !!(
        payload?.newSalary ||
        payload?.changeTypeCode ||
        payload?.executionDate
      ) && this.canManageEmploymentContractInformation
    );
  }

  public get jobTitleGroupNotFoundText(): string {
    if (this.isSeniorityPromotionSelected) {
      return "Grupo de cargo do colaborador não disponível para promoção por senioridade.";
    }

    if (this.meritSettingsLoadError) {
      return "Não foi possível carregar as regras de mérito.";
    }

    return "Nenhum grupo de cargo encontrado.";
  }

  public get jobTitleNotFoundText(): string {
    if (this.isSeniorityPromotionSelected) {
      return "Nenhuma função com senioridade superior ou igual disponível neste grupo de cargo.";
    }

    return "Nenhuma função encontrada.";
  }

  public get shouldShowNoSeniorityGroupAlert(): boolean {
    if (this.isDetailsMode || !this.isSeniorityPromotionSelected || this.isLoadingJobTitleGroups) {
      return false;
    }

    const employeeGroupId = this.data?.employee?.jobTitleGroupId;
    if (!employeeGroupId) {
      return true;
    }

    const employeeGroup = this.allJobTitleGroups.find(group => group.id === employeeGroupId);
    return !!this.hasLoadedJobTitleGroups && (!employeeGroup || !employeeGroup.hasSeniority);
  }

  private get canEditFieldsToApprovePromotion(): boolean {
    return (
      this.data?.solicitationDetails?.hasStepActionType(
        ApprovalScopeActionType.ERP_INTEGRATION.value
      ) &&
      this.canExecute
    );
  }

  private get canEditDangerousness(): boolean {
    return this.canExecute && this.hasAnyStepAction(
      ApprovalScopeActionType.ERP_INTEGRATION,
      ApprovalScopeActionType.SHOW_FIELDS_TO_APPROVE_NEW_JOBTITLE_TRAINED,
      ApprovalScopeActionType.SHOW_FIELD_TO_APPROVE_UNHEALTHINESS
    );
  }

  private get canEditUnhealthiness(): boolean {
    return this.canExecute && this.hasAnyStepAction(
      ApprovalScopeActionType.ERP_INTEGRATION,
      ApprovalScopeActionType.SHOW_FIELDS_TO_APPROVE_NEW_JOBTITLE_TRAINED,
      ApprovalScopeActionType.SHOW_FIELD_TO_APPROVE_UNHEALTHINESS
    );
  }

  private hasAnyStepAction(...actions: ApprovalScopeActionType[]): boolean {
    return actions.some(action => this.data?.solicitationDetails?.hasStepActionType(action.value));
  }

  public get canShowExecutionDate(): boolean {
    return resolveCanShowExecutionDate(this._erpFieldVisibilityParams);
  }

  private get _erpFieldVisibilityParams() {
    return buildErpApprovalFieldVisibilityParams(
      this.data?.solicitationDetails,
      this.canExecute,
      this.canManageEmploymentContractInformation,
      this.canEditFieldsToApprovePromotion
    );
  }

  private get canExecute(): boolean {
    return !!(
      this.data?.solicitation?.status === "PENDING" &&
      this.data?.solicitationDetails?.permission?.canExecute
    );
  }
}
